import { createApiRef, createRouteRef, createPlugin, createApiFactory, configApiRef, githubAuthApiRef, errorApiRef, createComponentExtension, createRoutableExtension } from '@backstage/core-plugin-api';
import { Octokit } from 'octokit';
import { readGithubIntegrationConfigs } from '@backstage/integration';
import { ForwardedError } from '@backstage/errors';

const githubIssuesApiRef = createApiRef({
  id: "plugin.githubissues.service"
});
const githubIssuesApi = (githubAuthApi, configApi, errorApi) => {
  let octokit;
  const getOctokit = async () => {
    var _a;
    const githubConfig = readGithubIntegrationConfigs(
      (_a = configApi.getOptionalConfigArray("integrations.github")) != null ? _a : []
    )[0];
    const baseUrl = githubConfig.apiBaseUrl;
    const token = await githubAuthApi.getAccessToken(["repo"]);
    if (!octokit) {
      octokit = new Octokit({ auth: token, ...baseUrl && { baseUrl } });
    }
    return { graphql: octokit.graphql, hostname: githubConfig.host };
  };
  const fetchIssuesByRepoFromGithub = async (repos, itemsPerRepo, {
    filterBy,
    orderBy = {
      field: "UPDATED_AT",
      direction: "DESC"
    }
  } = {}) => {
    const { graphql, hostname } = await getOctokit();
    const safeNames = [];
    const repositories = repos.filter((repo) => repo.locationHostname === hostname).map((repo) => {
      const [owner, name] = repo.name.split("/");
      const safeNameRegex = /-|\./gi;
      let safeName = name.replace(safeNameRegex, "");
      while (safeNames.includes(safeName)) {
        safeName += "x";
      }
      safeNames.push(safeName);
      return {
        safeName,
        name,
        owner
      };
    });
    let issuesByRepo = {};
    try {
      if (repositories.length === 0) {
        throw new Error(`No repositories found for ${hostname}`);
      }
      issuesByRepo = await graphql(
        createIssueByRepoQuery(repositories, itemsPerRepo, {
          filterBy,
          orderBy
        })
      );
    } catch (e) {
      if (e.data) {
        issuesByRepo = e.data;
      }
      errorApi.post(new ForwardedError("GitHub Issues Plugin failure", e));
    }
    return repositories.reduce((acc, { safeName, name, owner }) => {
      if (issuesByRepo[safeName]) {
        acc[`${owner}/${name}`] = issuesByRepo[safeName];
      }
      return acc;
    }, {});
  };
  return { fetchIssuesByRepoFromGithub };
};
function formatFilterValue(value) {
  if (Array.isArray(value)) {
    return `[ ${value.map(formatFilterValue).join(", ")}]`;
  }
  return typeof value === "string" ? `"${value}"` : `${value}`;
}
function createFilterByClause(filterBy) {
  if (!filterBy) {
    return "";
  }
  return Object.entries(filterBy).filter((value) => value).map(([field, value]) => {
    if (field === "states") {
      return `${field}: ${value.join(", ")}`;
    }
    return `${field}: ${formatFilterValue(value)}`;
  }).join(", ");
}
function createIssueByRepoQuery(repositories, itemsPerRepo, { filterBy, orderBy }) {
  const fragment = `
    fragment issues on Repository {
      issues(
        states: OPEN
        first: ${itemsPerRepo}
        filterBy: { ${createFilterByClause(filterBy)} }
        orderBy: { field: ${orderBy == null ? void 0 : orderBy.field}, direction: ${orderBy == null ? void 0 : orderBy.direction} }
      ) {
        totalCount
        edges {
          node {
            assignees(first: 10) {
              edges {
                node {
                  avatarUrl
                  login
                }
              }
            }
            author {
              login
            }
            repository {
              nameWithOwner
            }
            title
            url
            participants {
              totalCount
            }
            updatedAt
            createdAt
            comments(last: 1) {
              totalCount
            }
          }
        }
      }
    }
  `;
  const query = `
    ${fragment}

    query {
      ${repositories.map(
    ({ safeName, name, owner }) => `
        ${safeName}: repository(name: "${name}", owner: "${owner}") {
          ...issues
        }
      `
  )}
    }
  `;
  return query;
}

const rootRouteRef = createRouteRef({
  id: "github-issues"
});

const githubIssuesPlugin = createPlugin({
  id: "github-issues",
  apis: [
    createApiFactory({
      api: githubIssuesApiRef,
      deps: {
        configApi: configApiRef,
        githubAuthApi: githubAuthApiRef,
        errorApi: errorApiRef
      },
      factory: ({ configApi, githubAuthApi, errorApi }) => githubIssuesApi(githubAuthApi, configApi, errorApi)
    })
  ],
  routes: {
    root: rootRouteRef
  }
});
const GithubIssuesCard = githubIssuesPlugin.provide(
  createComponentExtension({
    name: "GithubIssuesCard",
    component: {
      lazy: () => import('./index-CEs9im6U.esm.js').then((m) => m.GithubIssues)
    }
  })
);
const GithubIssuesPage = githubIssuesPlugin.provide(
  createRoutableExtension({
    name: "GithubIssuesPage",
    component: () => import('./index-CEs9im6U.esm.js').then((m) => m.GithubIssues),
    mountPoint: rootRouteRef
  })
);

export { GithubIssuesPage as G, githubIssuesPlugin as a, GithubIssuesCard as b, githubIssuesApiRef as g };
//# sourceMappingURL=index-CVVu9uLW.esm.js.map
